/**
 * Begin the Microsoft Graph mailbox connect (F20-a spec FR-3; F20-i spec FR-17, FR-19).
 *
 * A **full-page browser navigation**, never an in-SPA `fetch` or a router navigate: Spring Security's
 * OAuth2 authorization entry point answers with a `302` to Entra, which only a real navigation can
 * follow. The path is resolved against `VITE_API_BASE_URL` — the base the HTTP client uses — so it works
 * same-origin behind the dev proxy and cross-host against a deployed `api.*` address (AD #33).
 *
 * The query is the F20-i Wave-1 contract's `ConnectStartQuery`: `mode=automatic` only for the automatic
 * connect (`nextStep: CONNECT_NOW`) — no `mode` is the interactive connect of *Try again* and *Reconnect* —
 * and `returnTo`, the page to come back to (`connectReturnPath`), omitted when there is none, which the
 * backend reads as `/`.
 *
 * **This module is the assign and nothing else** — no `import.meta.env.DEV` branch, no API call. That
 * keeps it the one mockable navigation seam, and lets a test assert it is called only off the stub path;
 * the branch lives in the caller, `MailboxConnectProvider` (F20-i WI-1.3 plan D-6), as F5-a splits
 * `startMicrosoftSignIn.ts` from the gate in `LoginRoute`.
 */

/** The framework OAuth2 authorization entry point for the `microsoft-mailbox` registration. */
const MAILBOX_AUTHORIZATION_PATH = '/oauth2/authorization/microsoft-mailbox';

export interface MailboxConnectStart {
    /** The automatic connect (`mode=automatic`); `false` is the interactive one. */
    readonly automatic: boolean;
    /** The relative CRM path to return to, or `undefined` for none. */
    readonly returnTo: string | undefined;
}

/** The authorization URL for a start — exported so the URL is a unit test, not a navigation. */
export function mailboxConnectUrl({ automatic, returnTo }: MailboxConnectStart): string {
    const baseUrl = import.meta.env.VITE_API_BASE_URL ?? '';
    const path = baseUrl
        ? `${baseUrl.replace(/\/$/, '')}${MAILBOX_AUTHORIZATION_PATH}`
        : MAILBOX_AUTHORIZATION_PATH;
    const query = new URLSearchParams();
    if (automatic) {
        query.set('mode', 'automatic');
    }
    if (returnTo !== undefined) {
        query.set('returnTo', returnTo);
    }
    const text = query.toString();
    return text === '' ? path : `${path}?${text}`;
}

export function startMailboxConnect(start: MailboxConnectStart): void {
    // A full-page assignment: the browser leaves the SPA and follows the backend's 302 to Entra.
    window.location.assign(mailboxConnectUrl(start));
}
